import type { BulkRefundResult } from '@/lib/api/payments';

export interface BulkRefundReason {
  message: string;
  count: number;
}

export interface BulkRefundSummary {
  tone: 'success' | 'partial' | 'failed';
  total: number;
  /** Refunded or voided: money is back with the shopper or was never taken. */
  refunded: number;
  /** Already refunded before this request: nothing to do, nothing owed. */
  skipped: number;
  failedIds: string[];
  /** Distinct failure reasons, most frequent first. */
  reasons: BulkRefundReason[];
}

const MAX_REASONS = 3;

/**
 * Reads the per-payment results of a bulk refund into what the operator needs to see: how many
 * went through, which ones did not, and why. #TAG-3003
 */
export function summarizeBulkRefund(result: BulkRefundResult): BulkRefundSummary {
  const failed = result.results.flatMap((r) => (r.outcome === 'failed' ? [r] : []));
  const refunded = result.refundedCount + result.voidedCount;

  const countsByMessage = failed.reduce<Map<string, number>>(
    (counts, r) => new Map(counts).set(r.message, (counts.get(r.message) ?? 0) + 1),
    new Map(),
  );
  const reasons = [...countsByMessage.entries()]
    .map(([message, count]) => ({ message, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_REASONS);

  const tone = failed.length === 0 ? 'success' : refunded > 0 ? 'partial' : 'failed';

  return {
    tone,
    total: result.results.length,
    refunded,
    skipped: result.skippedCount,
    failedIds: failed.map((r) => r.paymentId),
    reasons,
  };
}
